/**
 * @file: ErpSyncLogsScreen.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Every order sync attempt the store has made, newest first, filterable by
 * direction and by the mechanism that carried it. A log is a snapshot taken
 * at sync time, so it keeps reading correctly even after the order or the
 * integration behind it changes.
 */

import { useErpProviders } from '@euler/app/flows/erp/functions/useErpProviders';
import {
  kErpIntegrationTypeLabel,
  kErpSyncDirectionLabel,
  kErpSyncTypeLabel,
} from '@euler/app/flows/erp/utils/erpSyncLabels';
import { Center } from '@euler/components/Center';
import { Colors, StatusColors } from '@euler/components/Colors';
import { SegmentControl } from '@euler/components/SegmentControl';
import { FontFamily } from '@euler/components/typography';
import { useInfiniteListWithCursor } from '@euler/functions/useInfiniteListWithCursor';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { StoreErpOrderSyncLog } from '@euler/model/entity';
import {
  ErpIntegrationType,
  ErpSyncDirection,
  ErpSyncResult,
} from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import dayjs from 'dayjs';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kLimit = 20;
const keyExtractor = (log: StoreErpOrderSyncLog) => log.id;

/**
 * Filter options carry `undefined` in the first slot for "all", so the
 * segment index maps straight onto the search param without a lookup.
 */
const kDirectionOptions: (ErpSyncDirection | undefined)[] = [
  undefined,
  ErpSyncDirection.Inbound,
  ErpSyncDirection.Outbound,
];

const kIntegrationTypeOptions: (ErpIntegrationType | undefined)[] = [
  undefined,
  ErpIntegrationType.Hosted,
  ErpIntegrationType.Extension,
  ErpIntegrationType.Openapi,
];

const formatTime = (value?: string | Date | null) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '--';

export const ErpSyncLogsScreen = wrapNavigatorScreen(
  () => {
    return <Content />;
  },
  {
    title: '同步日志',
  },
);

const Content = memo(() => {
  const { storeErpIntegrationService } = useServiceFactory();
  const { bottom } = useSafeAreaInsets();
  const [directionIndex, setDirectionIndex] = useState(0);
  const [typeIndex, setTypeIndex] = useState(0);

  const syncDirection = kDirectionOptions[directionIndex];
  const integrationType = kIntegrationTypeOptions[typeIndex];

  const [loaded, setLoaded] = useState(false);

  const { replace, fetchMore, data, status } = useInfiniteListWithCursor<
    StoreErpOrderSyncLog,
    string
  >({
    limit: kLimit,
    keyExtractor,
    query: async nextKey =>
      await storeErpIntegrationService.searchErpOrderSyncLogs(
        { syncDirection, integrationType },
        'desc',
        nextKey,
        kLimit,
      ),
  });

  // `replace` rather than `fetch`: changing a filter restarts the cursor, and
  // a key from the previous result set does not address anything in the new
  // one. It also drops a response that arrives after the filter moved on,
  // which `fetch` cannot do — it refuses to start while one is in flight.
  useEffect(() => {
    void replace().finally(() => setLoaded(true));
  }, [replace, syncDirection, integrationType]);

  const renderItem: ListRenderItem<StoreErpOrderSyncLog> = usePersistFn(
    ({ item }) => <LogCell log={item} />,
  );

  return (
    <View css="flex: 1; background-color: #f7f7f7;">
      <View css="background-color: #fff; padding: 12px 15px;">
        <SegmentControl
          selectedIndex={directionIndex}
          segments={[
            '全部方向',
            kErpSyncDirectionLabel[ErpSyncDirection.Inbound],
            kErpSyncDirectionLabel[ErpSyncDirection.Outbound],
          ]}
          onSelectedIndexChange={setDirectionIndex}
        />
        <SegmentControl
          style={{ marginTop: 10 }}
          selectedIndex={typeIndex}
          segments={[
            '全部方式',
            kErpIntegrationTypeLabel[ErpIntegrationType.Hosted],
            kErpIntegrationTypeLabel[ErpIntegrationType.Extension],
            kErpIntegrationTypeLabel[ErpIntegrationType.Openapi],
          ]}
          onSelectedIndexChange={setTypeIndex}
        />
      </View>

      <FlatList
        data={data}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        onEndReached={() => fetchMore()}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: bottom + 16 }}
        ListEmptyComponent={
          !loaded || status.isLoading ? null : (
            <Center css="flex: 1; padding: 40px 32px;">
              <Text css="font-size: 14px; color: #667085; text-align: center;">
                暂无同步记录
              </Text>
            </Center>
          )
        }
        ListHeaderComponent={
          !status.loadedDataLength && (!loaded || status.isLoading) ? (
            <Center css="padding: 40px 0;">
              <ActivityIndicator color={Colors.Gray2} />
            </Center>
          ) : null
        }
        ListFooterComponent={
          status.loadedDataLength > 0 && status.isLoading ? (
            <ActivityIndicator css="margin-top: 10px;" color={Colors.Gray2} />
          ) : status.loadedDataLength > 0 && !status.hasMore ? (
            <Text css="margin-top: 12px; font-size: 12px; color: #98a2b3; text-align: center;">
              没有更多了
            </Text>
          ) : null
        }
      />
    </View>
  );
});

const LogCell = memo(({ log }: { log: StoreErpOrderSyncLog }) => {
  const { getProviderName } = useErpProviders();
  const succeeded = log.syncResult === ErpSyncResult.Success;

  const vehicle = useMemo(
    () =>
      [log.vehicleBrandName, log.vehicleModel].filter(Boolean).join(' · ') ||
      log.vehicleName ||
      '',
    [log.vehicleBrandName, log.vehicleModel, log.vehicleName],
  );

  return (
    <View
      css={`
        background-color: #fff;
        border-radius: 12px;
        margin: 12px 15px 0 15px;
        padding: 14px 16px;
      `}
    >
      <View css="flex-direction: row; align-items: center;">
        <Text
          css={`
            flex: 1;
            font-family: ${FontFamily.NotoSans.Medium};
            font-size: 15px;
            color: #101828;
          `}
          numberOfLines={1}
        >
          {log.licensePlateNo || '未知车牌'}
        </Text>
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Medium};
            font-size: 13px;
            color: ${succeeded ? StatusColors.Success : StatusColors.Danger};
          `}
        >
          {succeeded ? '同步成功' : '同步失败'}
        </Text>
      </View>

      <View css="flex-direction: row; flex-wrap: wrap; margin-top: 8px;">
        <Tag text={getProviderName(log.erpProviderId, log.erpProviderId)} />
        <Tag text={kErpIntegrationTypeLabel[log.integrationType]} />
        <Tag text={kErpSyncDirectionLabel[log.syncDirection]} />
        <Tag text={kErpSyncTypeLabel[log.syncType]} />
      </View>

      {!!vehicle && <Row label="车辆" value={vehicle} />}
      <Row label="来源单号" value={log.sourceOrderno || '--'} />
      <Row label="开单时间" value={formatTime(log.sourceOrderTimestamp)} />
      {!!log.resultOrderno && (
        <Row label="生成单号" value={log.resultOrderno} />
      )}
      <Row label="接车顾问" value={log.serviceAgentName || '--'} />
      <Row label="同步时间" value={formatTime(log.finishedAt)} />
      {!!log.requestedByName && (
        <Row label="操作人" value={log.requestedByName} />
      )}
      {!!log.syncErrorMsg && (
        <Row label="失败原因" value={log.syncErrorMsg} danger />
      )}
    </View>
  );
});

const Tag = memo(({ text }: { text: string }) => (
  <View
    css={`
      background-color: #f2f4f7;
      border-radius: 4px;
      padding: 2px 8px;
      margin-right: 6px;
      margin-bottom: 6px;
    `}
  >
    <Text css="font-size: 11px; color: #475467;">{text}</Text>
  </View>
));

const Row = memo(
  ({
    label,
    value,
    danger,
  }: {
    label: string;
    value: string;
    danger?: boolean;
  }) => (
    <View css="flex-direction: row; margin-top: 6px;">
      <Text css="width: 68px; font-size: 13px; color: #98a2b3;">{label}</Text>
      <Text
        css={`
          flex: 1;
          font-size: 13px;
          color: ${danger ? StatusColors.Danger : '#344054'};
        `}
      >
        {value}
      </Text>
    </View>
  ),
);
